import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

import { getFolderContent, API, compressAll, compressSelected, downloadFiles } from "./api";
import ExplorerView from "./components/ExplorerView";
import ActionBar from "./components/ActionBar";
import Breadcrumb from "./components/Breadcrumb";
import ConfirmDialog from "./components/ConfirmDialog";
import UploadProgress from "./components/UploadProgress";
import NotifyCenter from "./components/NotifyCenter";
import LoginPopup from "./LoginPopup";
import namingRules from "./namingRules";

import "./App.css";

const allowedExt = [
  "jpg", "jpeg", "png", "gif", "webp",
  "mp4", "mov", "avi", "mkv", "webm",
  "m4v", "wmv", "flv", "3gp",
  "pdf",
];

const normalize = (str) =>
  str
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-");

export default function App() {
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [userName, setUserName] = useState(localStorage.getItem("userName") || "");
  const [showLogin, setShowLogin] = useState(!role);

  const [path, setPath] = useState("");
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  const [showUploadFolderModal, setShowUploadFolderModal] = useState(false);

  const [search, setSearch] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [folderFiles, setFolderFiles] = useState(null);

  const [confirmData, setConfirmData] = useState(null);

  const [notifyCenter, setNotifyCenter] = useState("");
  const [notifyType, setNotifyType] = useState("success");

  const showPopup = (msg, type = "success") => {
    setNotifyCenter(msg);
    setNotifyType(type);
    setTimeout(() => setNotifyCenter(""), 2000);
  };

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [eta, setEta] = useState(0);

  const lastLoadedRef = useRef(0);
  const lastTimeRef = useRef(Date.now());

  const [compressing, setCompressing] = useState(false);
  const [compressResult, setCompressResult] = useState(null);

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedDownload, setSelectedDownload] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const [showCompressSelectModal, setShowCompressSelectModal] = useState(false);
  const [selectedCompress, setSelectedCompress] = useState([]);
  const [compressSelectResult, setCompressSelectResult] = useState(null);

  const getUploadNote = () => {
    if (!path) return "Chỉ upload file hợp lệ.";

    const rootFolder = path.split("/")[0];
    const key = normalize(rootFolder);
    const rules = namingRules[key];

    if (!rules) return "Chỉ upload file hợp lệ.";

    return `- Đặt file theo tên: ${rules.prefix}_${rules.nameLabel}.png
- Ví dụ: ${rules.prefix}_${rules.example}.png`;
  };

  // Load folder
  useEffect(() => {
    if (!showLogin) {
      loadFolder(path);
    }
  }, [path, showLogin]);

  const loadFolder = async (p) => {
    const data = await getFolderContent(p);
    setFolders(data.folders || []);
    setImages(data.media || []);
  };

  const goIntoFolder = (folder) =>
    setPath(path ? `${path}/${folder}` : folder);

  const goBack = () => {
    if (!path) return;
    const parts = path.split("/");
    parts.pop();
    setPath(parts.join("/") || "");
  };

  const systemFiles = ["thumbs.db", ".ds_store", "desktop.ini"];

  const validateFiles = (files) => {
    for (let f of files) {
      if (systemFiles.includes(f.name.toLowerCase())) continue;
      const ext = f.name.split(".").pop().toLowerCase();
      if (!allowedExt.includes(ext)) {
        showPopup(`❌ Không hỗ trợ: ${f.name}`, "error");
        return false;
      }
    }
    return true;
  };

  // ================== CREATE FOLDER ==================
  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await axios.post(`${API}/folder/create`, {
        folderName: newFolderName,
        parent: path,
      });
      loadFolder(path);
      showPopup("📁 Tạo thư mục thành công!");
    } catch {
      showPopup("⚠️ Tên thư mục đã tồn tại!", "warning");
    }

    setShowCreateModal(false);
    setNewFolderName("");
  };

  // ================== UPLOAD ==================
  const handleUpload = async (url, formData) => {
    setUploading(true);
    setProgress(0);

    lastLoadedRef.current = 0;
    lastTimeRef.current = Date.now();

    try {
      await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { path: path || "" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          setProgress(percent);

          const now = Date.now();
          const dt = (now - lastTimeRef.current) / 1000;
          const dl = e.loaded - lastLoadedRef.current;

          if (dt > 0) {
            const speedMB = dl / 1024 / 1024 / dt;
            setSpeed(speedMB.toFixed(2));
            const remain = e.total - e.loaded;
            setEta((remain / (speedMB * 1024 * 1024)).toFixed(1));
          }

          lastLoadedRef.current = e.loaded;
          lastTimeRef.current = now;
        },
      });

      loadFolder(path);
      showPopup("🎉 Upload thành công!");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Lỗi không xác định";
      showPopup(`❌ Upload thất bại: ${msg}`, "error");
      console.error("Upload error:", err?.response?.status, err?.response?.data);
    }

    setUploading(false);
  };

  const uploadImages = async () => {
    if (!selectedFiles || !validateFiles(selectedFiles)) return;
    setShowUploadImageModal(false);

    const form = new FormData();
    [...selectedFiles].forEach((f) => form.append("files", f));
    await handleUpload(`${API}/upload`, form);
  };

  const uploadFolder = async () => {
    const filteredFolderFiles = folderFiles
      ? [...folderFiles].filter((f) => !systemFiles.includes(f.name.toLowerCase()))
      : null;
    if (!filteredFolderFiles || !validateFiles(filteredFolderFiles)) return;

    const form = new FormData();
    filteredFolderFiles.forEach((f) =>
      form.append("files", f, f.webkitRelativePath)
    );

    setShowUploadFolderModal(false);
    await handleUpload(`${API}/upload-folder`, form);
  };

  // ================== DOWNLOAD ==================
  const toggleDownloadSelect = (name) => {
    setSelectedDownload((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDownload.length === images.length) {
      setSelectedDownload([]);
    } else {
      setSelectedDownload(images.map((i) => i.name));
    }
  };

  const handleDownload = async () => {
    if (selectedDownload.length === 0) return;
    setDownloading(true);
    try {
      await downloadFiles(path, selectedDownload);
      showPopup("✅ Tải về thành công!");
      setShowDownloadModal(false);
      setSelectedDownload([]);
    } catch {
      showPopup("❌ Tải về thất bại!", "error");
    }
    setDownloading(false);
  };

  // ================== COMPRESS SINGLE FILE ==================
  const handleCompressImage = async (name) => {
    try {
      const result = await compressSelected(path, [name], true);
      loadFolder(path);
      if (result?.compressed > 0) showPopup("✅ Nén thành công!");
      else showPopup("⚠️ Không thể nén thêm!", "warning");
    } catch {
      showPopup("❌ Nén thất bại!", "error");
    }
  };

  const toggleCompressSelect = (name) =>
    setSelectedCompress((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  const toggleCompressSelectAll = () =>
    setSelectedCompress(
      selectedCompress.length === images.length ? [] : images.map((i) => i.name)
    );

  const handleCompressSelected = async () => {
    if (selectedCompress.length === 0) return;
    setCompressing(true);
    setCompressSelectResult(null);
    try {
      const result = await compressSelected(path, selectedCompress);
      setCompressSelectResult(result);
      loadFolder(path);
    } catch {
      showPopup("❌ Nén thất bại!", "error");
    }
    setCompressing(false);
  };


  const handleResize5MB = () => {
    const label = path ? `"${path.split("/").pop()}"` : "toàn hệ thống";
    setConfirmData({
      message: `Resize tất cả ảnh trong thư mục ${label} về ≤5MB?`,
      onConfirm: async () => {
        setConfirmData(null);
        setCompressing(true);
        setCompressResult(null);
        try {
          const result = await compressAll(path, false, 5);
          setCompressResult(result);
          loadFolder(path);
        } catch {
          showPopup("❌ Resize thất bại!", "error");
        }
        setCompressing(false);
      },
    });
  };

  const handleCompressAll = () => {
    const isGlobal = !path;
    const msg = isGlobal
      ? "Nén toàn bộ ảnh/video trong HỆ THỐNG về ≤1MB? (bao gồm tất cả thư mục con, kể cả ảnh đã < 1MB)"
      : `Nén tất cả ảnh/video trong thư mục "${path.split("/").pop()}" về ≤1MB?`;
    setConfirmData({
      message: msg,
      onConfirm: async () => {
        setConfirmData(null);
        setCompressing(true);
        setCompressResult(null);
        try {
          const result = await compressAll(path, isGlobal);
          setCompressResult(result);
          loadFolder(path);
        } catch {
          showPopup("❌ Nén thất bại!", "error");
        }
        setCompressing(false);
      },
    });
  };

  const askDeleteImage = (name) =>
    setConfirmData({
      message: `Xóa file "${name}"?`,
      onConfirm: () => deleteImage(name),
    });

  const deleteImage = async (name) => {
  try {
    await axios.delete(`${API}/delete-file`, {
      params: {
        path: path || "",
        name: name,
      },
    });

    setConfirmData(null);
    loadFolder(path);
    showPopup("🗑 Xóa file thành công!");
  } catch (err) {
    console.error("Delete image failed:", err);
    showPopup("❌ Xóa file thất bại!", "error");
    setConfirmData(null);
  }
};


  const renameImage = async (oldName, newName) => {
    try {
      await axios.post(`${API}/rename-file`, { path, oldName, newName });
      loadFolder(path);
      showPopup("✔ Đổi tên file thành công!");
    } catch {
      showPopup("⚠️ Tên file đã tồn tại!", "warning");
    }
  };

  const askDeleteFolder = (folder) =>
    setConfirmData({
      message: `Xóa thư mục "${folder}"?`,
      onConfirm: () => deleteFolder(folder),
    });

  const deleteFolder = async (folder) => {
    const fullPath = path ? `${path}/${folder}` : folder;

    await axios.delete(`${API}/delete-folder`, {
      params: { path: fullPath },
    });

    setConfirmData(null);
    loadFolder(path);
    showPopup("🗑 Xóa thư mục thành công!");
  };

  const renameFolder = async (folderName, newName) => {
    try {
      await axios.post(`${API}/rename-folder`, {
        parent: path,
        oldName: folderName,
        newName,
      });
      loadFolder(path);
      showPopup("📁 Đổi tên thư mục thành công!");
    } catch {
      showPopup("⚠️ Tên thư mục đã tồn tại!", "warning");
    }
  };

  if (showLogin) {
    return (
      <LoginPopup
        onLogin={(r, name) => {
          localStorage.setItem("role", r);
          localStorage.setItem("userName", name);
          setRole(r);
          setUserName(name);
          setShowLogin(false);
        }}
      />
    );
  }

  return (
    <div className="app-container">
      <header className="main-header">
        <img src="/logo.png" className="header-logo" />
        <div className="header-title">
          Ảnh Nội Bộ Bệnh Viện
          <span>Hùng Vương Gia Lai</span>
        </div>

        {userName && (
          <span className="header-username">{userName}</span>
        )}
        <button
          className="logout-btn"
          onClick={() => {
            localStorage.removeItem("role");
            localStorage.removeItem("userName");
            setRole(null);
            setUserName("");
            setPath("");
            setShowLogin(true);
          }}
        >
          Đăng xuất
        </button>
      </header>

      <div className="content-wrapper">
        <Breadcrumb path={path} setPath={setPath} />

        <ActionBar
          role={role}
          path={path}
          onCreate={() => setShowCreateModal(true)}
          onUploadImage={() => setShowUploadImageModal(true)}
          onUploadFolder={() => setShowUploadFolderModal(true)}
          onSearch={setSearch}
          onCompressAll={handleCompressAll}
          onResize5MB={handleResize5MB}
          onCompressSelected={() => { setSelectedCompress([]); setCompressSelectResult(null); setShowCompressSelectModal(true); }}
          onDownload={() => { setSelectedDownload([]); setShowDownloadModal(true); }}
        />

        <ExplorerView
          role={role}
          folders={folders}
          images={images}
          path={path}
          search={search}
          onOpenFolder={goIntoFolder}
          onBack={goBack}
          onDeleteFolder={role === "admin" ? askDeleteFolder : null}
          onDeleteImage={role === "admin" ? askDeleteImage : null}
          onRenameFolder={role === "admin" ? renameFolder : null}
          onRenameImage={renameImage}
          onCompressImage={handleCompressImage}
        />
      </div>

      {uploading && (
        <UploadProgress progress={progress} speed={speed} eta={eta} />
      )}

      {confirmData && (
        <ConfirmDialog
          message={confirmData.message}
          onConfirm={confirmData.onConfirm}
          onCancel={() => setConfirmData(null)}
        />
      )}

      {notifyCenter && (
        <NotifyCenter message={notifyCenter} type={notifyType} />
      )}


      {role === "admin" && showCreateModal && (
        <div className="modal">
          <div className="modal-box">
            <h3>Tạo thư mục mới</h3>

            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Tên thư mục..."
            />

            <p className="note" style={{ whiteSpace: "pre-line" }}>
              {getUploadNote()}
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={createFolder}>
                Tạo
              </button>
              <button
                className="btn cancel"
                onClick={() => setShowCreateModal(false)}
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadImageModal && (
        <div className="modal">
          <div className="modal-box">
            <h3>Upload ảnh / video / PDF</h3>

            <input
              type="file"
              multiple
              onChange={(e) => setSelectedFiles(e.target.files)}
            />

            <p className="note" style={{ whiteSpace: "pre-line" }}>
              {getUploadNote()}
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={uploadImages}>
                Upload
              </button>
              <button
                className="btn cancel"
                onClick={() => setShowUploadImageModal(false)}
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPLOAD FOLDER */}
      {role === "admin" && showUploadFolderModal && (
        <div className="modal">
          <div className="modal-box">
            <h3>Upload thư mục</h3>

            <input
              type="file"
              webkitdirectory="true"
              multiple
              onChange={(e) => setFolderFiles(e.target.files)}
            />

            <p className="note" style={{ whiteSpace: "pre-line" }}>
              {getUploadNote()}
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={uploadFolder}>
                Upload
              </button>
              <button
                className="btn cancel"
                onClick={() => setShowUploadFolderModal(false)}
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DOWNLOAD MODAL */}
      {showDownloadModal && (
        <div className="modal">
          <div className="modal-box" style={{ maxWidth: 520, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
            <h3>Tải file về</h3>

            <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 8px", fontWeight: 600, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={selectedDownload.length === images.length && images.length > 0}
                onChange={toggleSelectAll}
              />
              Chọn tất cả ({images.length} file)
            </label>

            <div style={{ overflowY: "auto", flex: 1, border: "1px solid #ddd", borderRadius: 8, padding: "4px 0" }}>
              {images.map((item) => (
                <label
                  key={item.name}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "7px 14px", cursor: "pointer",
                    background: selectedDownload.includes(item.name) ? "#e8f0fe" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDownload.includes(item.name)}
                    onChange={() => toggleDownloadSelect(item.name)}
                  />
                  <span style={{ flex: 1, fontSize: 13 }}>{item.name}</span>
                  {item.size > 0 && (
                    <span style={{ fontSize: 11, color: "#999" }}>
                      {item.size < 1024 * 1024
                        ? `${(item.size / 1024).toFixed(1)} KB`
                        : `${(item.size / 1024 / 1024).toFixed(1)} MB`}
                    </span>
                  )}
                </label>
              ))}
              {images.length === 0 && (
                <p style={{ textAlign: "center", color: "#999", padding: 16 }}>Không có file nào</p>
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: 14 }}>
              <button
                className="btn"
                onClick={handleDownload}
                disabled={selectedDownload.length === 0 || downloading}
              >
                {downloading ? "Đang tải..." : `Tải về (${selectedDownload.length})`}
              </button>
              <button className="btn cancel" onClick={() => setShowDownloadModal(false)}>
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COMPRESS SELECTED MODAL */}
      {showCompressSelectModal && (
        <div className="modal">
          <div className="modal-box" style={{ maxWidth: 520, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
            <h3>Nén ảnh chọn lọc</h3>

            {compressSelectResult ? (
              <>
                <table style={{ width: "calc(100% - 44px)", margin: "16px 22px", borderCollapse: "collapse", fontSize: 13 }}>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "9px 0", color: "#64748B" }}>Đã nén thành công</td>
                      <td style={{ padding: "9px 0", textAlign: "right", fontWeight: 700, color: compressSelectResult.compressed > 0 ? "#16A34A" : "#334155" }}>{compressSelectResult.compressed}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "9px 0", color: "#64748B" }}>Bỏ qua (dưới 200KB hoặc lỗi)</td>
                      <td style={{ padding: "9px 0", textAlign: "right", fontWeight: 700, color: "#334155" }}>{compressSelectResult.skipped}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="modal-actions">
                  <button className="btn" onClick={() => { setShowCompressSelectModal(false); setCompressSelectResult(null); }}>Đóng</button>
                </div>
              </>
            ) : (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 22px 8px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={selectedCompress.length === images.length && images.length > 0}
                    onChange={toggleCompressSelectAll}
                  />
                  Chọn tất cả ({images.length} file)
                </label>

                <div style={{ overflowY: "auto", flex: 1, border: "1px solid #E2E8F0", borderRadius: 8, margin: "0 22px", padding: "4px 0" }}>
                  {images.map((item) => (
                    <label
                      key={item.name}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "7px 14px", cursor: "pointer",
                        background: selectedCompress.includes(item.name) ? "#EFF6FF" : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCompress.includes(item.name)}
                        onChange={() => toggleCompressSelect(item.name)}
                      />
                      <span style={{ flex: 1, fontSize: 13 }}>{item.name}</span>
                      {item.size > 0 && (
                        <span style={{ fontSize: 11, color: "#94A3B8" }}>
                          {item.size < 1024 * 1024 ? `${(item.size / 1024).toFixed(1)} KB` : `${(item.size / 1024 / 1024).toFixed(1)} MB`}
                        </span>
                      )}
                    </label>
                  ))}
                  {images.length === 0 && (
                    <p style={{ textAlign: "center", color: "#94A3B8", padding: 16, fontSize: 13 }}>Không có file nào</p>
                  )}
                </div>

                <div className="modal-actions" style={{ marginTop: 14 }}>
                  <button
                    className="btn"
                    onClick={handleCompressSelected}
                    disabled={selectedCompress.length === 0 || compressing}
                  >
                    {compressing ? "Đang nén..." : `Nén (${selectedCompress.length} file)`}
                  </button>
                  <button className="btn cancel" onClick={() => setShowCompressSelectModal(false)}>Hủy</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* COMPRESS LOADING */}
      {compressing && (
        <div className="modal">
          <div className="modal-box" style={{ textAlign: "center" }}>
            <h3>Đang nén file...</h3>
            <p style={{ color: "#888" }}>Vui lòng chờ, quá trình này có thể mất vài phút.</p>
          </div>
        </div>
      )}

      {/* COMPRESS RESULT */}
      {compressResult && (
        <div className="modal">
          <div className="modal-box">
            <h3>Kết quả nén</h3>
            <table style={{ width: "calc(100% - 44px)", margin: "16px 22px", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                {[
                  ["Ảnh đã nén", compressResult.images_compressed, true],
                  ["Ảnh bỏ qua", compressResult.images_skipped, false],
                  ["PDF đã nén", compressResult.pdfs_compressed, true],
                  ["PDF bỏ qua", compressResult.pdfs_skipped, false],
                  ["Video đã nén", compressResult.videos_compressed, true],
                  ["Video bỏ qua", compressResult.videos_skipped, false],
                ].map(([label, val, isGreen]) => (
                  <tr key={label} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "9px 0", color: "#64748B" }}>{label}</td>
                    <td style={{ padding: "9px 0", textAlign: "right", fontWeight: 700, color: isGreen && val > 0 ? "#16A34A" : "#334155" }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-actions">
              <button className="btn" onClick={() => setCompressResult(null)}>Đóng</button>
            </div>
          </div>
        </div>
      )}

      <div className="footer-info fancy-footer">
        <span className="footer-brand">
          © {new Date().getFullYear()} Hùng Vương Gia Lai
        </span>
        <span className="footer-brand">
          Powered by Phòng CNTT - leanhquan™
        </span>
      </div>
    </div>
  );
}
